import * as yup from "yup";
import createFormFieldComponents from "components/Shared/Forms/FormField";
import { Writeable } from "utils/typescriptUtilis";
import {
  SetSkuBrandOrderingPlatformsForSkuDatumInput,
  SkuComponentInput,
  SkuInput,
} from "__generated__/gql/graphql";
import * as skuSchemaFields from "utils/schemas/sku";
import * as skuComponentSchemaFields from "utils/schemas/skuComponent";
import * as skuBrandOrderingPlatformSchemaFields from "utils/schemas/skuBrandOrderingPlatform";
import type { Dimensions } from "utils/schemas/dimensions";
import type { TiHi } from "utils/schemas/tiHi";

export const skuFields = [
  "name",
  "shortName",
  "productLineId",
  "internalSkuId",
  "unitNetWeight",
  "unitNetWeightUnit",
  "grossPalletWeightLbs",
  "unitCogs",
  "unitSrp",
] as const;

export const skuComponentFields = [
  "skuComponentType",
  "upc",
  "grossWeightLbs",
] as const;

export const skuBrandOrderingPlatformFields = [
  "itemCode",
  "wholesaleUnitPrice",
] as const;

export const skuOrderingPlatformDatum = [
  "orderingPlatformId",
  ...skuBrandOrderingPlatformFields,
  "distributionCenterIds",
] as const;

export type SkuPrimitiveInputs = Required<
  Pick<Writeable<SkuInput>, typeof skuFields[number]>
>;

export type SkuComponentPrimitiveInputs = Required<
  Pick<Writeable<SkuComponentInput>, typeof skuComponentFields[number]>
>;

export type SkuComponentDatum = SkuComponentPrimitiveInputs & {
  dimensions?: Dimensions | null;
};

export type SkuOrderingPlatformDatum = Required<
  Pick<
    Writeable<SetSkuBrandOrderingPlatformsForSkuDatumInput>,
    typeof skuOrderingPlatformDatum[number]
  >
> & {
  distributionCenterIds: string[];
};

export type SkuInputs = SkuPrimitiveInputs & {
  tiHi?: TiHi | null;
  skuComponents?: SkuComponentDatum[];
  orderingPlatformData?: SkuOrderingPlatformDatum[];
};

export const skuSchema: yup.ObjectSchema<SkuInputs> = yup
  .object()
  .shape({
    productLineId: skuSchemaFields.productLineId,
    name: skuSchemaFields.name,
    shortName: skuSchemaFields.shortName.defined(),
    internalSkuId: skuSchemaFields.internalSkuId,
    unitNetWeight: skuSchemaFields.unitNetWeight,
    unitNetWeightUnit: skuSchemaFields.unitNetWeightUnit,
    grossPalletWeightLbs: skuSchemaFields.grossPalletWeightLbs,
    unitCogs: skuSchemaFields.unitCogs,
    unitSrp: skuSchemaFields.unitSrp,
    tiHi: skuSchemaFields.tiHi,
    skuComponents: yup.array().of(skuComponentSchemaFields.default),
    orderingPlatformData: yup.array().of(
      yup.object().shape({
        orderingPlatformId:
          skuBrandOrderingPlatformSchemaFields.orderingPlatformId,
        itemCode: skuBrandOrderingPlatformSchemaFields.itemCode,
        wholesaleUnitPrice:
          skuBrandOrderingPlatformSchemaFields.wholesaleUnitPrice,
        distributionCenterIds:
          skuBrandOrderingPlatformSchemaFields.distributionCenterIds,
      }),
    ),
  })
  .required();

export const { FormField, ControlledFormField } =
  createFormFieldComponents<SkuInputs>({
    schema: skuSchema,
  });
